import { useState, useEffect } from 'react';
import api from '@/api';
import { Loader2, FileText, Download, Search } from 'lucide-react';
import { toast } from 'sonner';
import FilterBar, { FilterSelect, FilterInput, FilterDate } from '@/components/FilterBar';
import useRepairCategories from '@/hooks/useRepairCategories';

const PAYMENT_COLORS = {
  paid:'text-[var(--success)] bg-[var(--success-muted)] border-[var(--success)]',
  unpaid:'text-[var(--warning)] bg-[var(--warning-muted)] border-[var(--warning)]',
  overdue:'text-[var(--error)] bg-[var(--error-muted)] border-[var(--error)]',
  estimate_received:'text-[#8B5CF6] bg-[#8B5CF6]/10 border-[#8B5CF6]',
  pending_approval:'text-[#F59E0B] bg-[#F59E0B]/10 border-[#F59E0B]',
};
const STATUS_LABELS = {
  paid: 'Paid', unpaid: 'Unpaid', overdue: 'Overdue',
  estimate_received: 'Estimate Received', pending_approval: 'Pending Approval',
};
const EMPTY = { payment_status:'', category:'', unit_search:'', vendor:'', invoice_search:'', date_from:'', date_to:'' };

export default function PortalInvoices() {
  const { names: CATS } = useRepairCategories();
  const [invoices, setInvoices] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState(EMPTY);

  const activeCount = Object.values(filters).filter(v => v !== '').length;
  const fmt = (n) => new Intl.NumberFormat('en-US',{style:'currency',currency:'USD'}).format(n||0);

  useEffect(() => { load(); }, [filters]);

  const load = async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([_,v]) => v !== ''));
      params.limit = 50;
      const res = await api.get('/invoices', { params });
      setInvoices(res.data.items);
      setTotal(res.data.total);
    } catch { toast.error('Failed to load invoices'); }
    finally { setLoading(false); }
  };

  const setF = (key, val) => setFilters(p => ({ ...p, [key]: val }));

  const totals = {
    total: invoices.reduce((a,i) => a+(i.amount||0), 0),
    paid: invoices.filter(i => i.payment_status==='paid').reduce((a,i) => a+(i.amount||0), 0),
    outstanding: invoices.filter(i => i.payment_status!=='paid').reduce((a,i) => a+(i.amount||0), 0),
  };

  return (
    <div className="space-y-5 animate-fade-in" data-testid="portal-invoices-page">
      <div>
        <h1 className="font-['Manrope'] font-bold text-2xl" style={{ color: 'var(--text-primary)' }}>My Invoices</h1>
        <p className="font-['IBM_Plex_Sans'] text-sm mt-1" style={{ color: 'var(--text-muted)' }}>{total} invoices</p>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-3 gap-4">
        {[{label:'Total Shown',value:fmt(totals.total)},{label:'Paid',value:fmt(totals.paid)},{label:'Outstanding',value:fmt(totals.outstanding)}].map(({label,value}) => (
          <div key={label} className="p-4 rounded-md border" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
            <p className="text-[10px] uppercase tracking-widest font-['IBM_Plex_Sans'] mb-2" style={{ color: 'var(--text-muted)' }}>{label}</p>
            <p className="text-xl font-bold font-['JetBrains_Mono']" style={{ color: 'var(--text-primary)' }}>{value}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <FilterBar onClear={() => setFilters(EMPTY)} activeCount={activeCount}>
        <div className="relative">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-faint)' }} />
          <input value={filters.invoice_search} onChange={e => setF('invoice_search', e.target.value)}
            placeholder="Search invoice #, repair ID..."
            className="w-full input-slate pl-8" />
        </div>
        <div className="flex flex-wrap gap-3">
          <FilterSelect label="Payment" value={filters.payment_status} onChange={v => setF('payment_status', v)}
            options={[{value:'estimate_received',label:'Estimate Received'},{value:'pending_approval',label:'Pending Approval'},{value:'unpaid',label:'Unpaid'},{value:'paid',label:'Paid'},{value:'overdue',label:'Overdue'}]} />
          <FilterSelect label="Category" value={filters.category} onChange={v => setF('category', v)}
            options={CATS.map(c => ({value:c,label:c}))} />
          <FilterInput label="Unit / VIN / Plate" value={filters.unit_search} onChange={v => setF('unit_search', v)} placeholder="ATL-001..." />
          <FilterInput label="Vendor" value={filters.vendor} onChange={v => setF('vendor', v)} placeholder="Shop name..." />
          <FilterDate label="From" value={filters.date_from} onChange={v => setF('date_from', v)} />
          <FilterDate label="To" value={filters.date_to} onChange={v => setF('date_to', v)} />
        </div>
      </FilterBar>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="animate-spin" size={24} style={{ color: 'var(--primary)' }} /></div>
      ) : invoices.length === 0 ? (
        <div className="py-16 text-center rounded-md border" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
          <FileText size={32} className="mx-auto mb-3" style={{ color: 'var(--border)' }} />
          <p className="font-['IBM_Plex_Sans']" style={{ color: 'var(--text-muted)' }}>No invoices match your filters</p>
        </div>
      ) : (
        <div className="space-y-3">
          {invoices.map(inv => (
            <div key={inv.id} className="flex items-center gap-5 p-5 rounded-md border transition-colors"
              style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}
              onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--primary)'}
              onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}
              data-testid={`portal-invoice-row-${inv.id}`}>
              <div className="w-10 h-10 rounded-md flex items-center justify-center flex-shrink-0" style={{ background: 'var(--bg)' }}>
                <FileText size={16} style={{ color: 'var(--text-muted)' }} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3">
                  <span className="font-['JetBrains_Mono'] text-sm font-bold" style={{ color: 'var(--primary)' }}>{inv.invoice_number}</span>
                  {inv.truck_unit_number && inv.truck_unit_number !== 'N/A' && <span className="text-xs font-['IBM_Plex_Sans']" style={{ color: 'var(--text-muted)' }}>Unit {inv.truck_unit_number}</span>}
                </div>
                <p className="text-sm font-['IBM_Plex_Sans'] mt-0.5" style={{ color: 'var(--text-primary)' }}>{inv.vendor_name||inv.category||'—'}</p>
                {inv.notes && <p className="text-xs font-['IBM_Plex_Sans'] mt-0.5 truncate" style={{ color: 'var(--text-faint)' }}>{inv.notes}</p>}
              </div>
              <div className="text-right flex-shrink-0">
                <p className="font-['JetBrains_Mono'] text-lg font-bold" style={{ color: 'var(--text-primary)' }}>{fmt(inv.amount)}</p>
                <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide border font-['IBM_Plex_Sans'] mt-1 ${PAYMENT_COLORS[inv.payment_status]||PAYMENT_COLORS.unpaid}`}>
                  {STATUS_LABELS[inv.payment_status] || inv.payment_status}
                </span>
              </div>
              {inv.pdf_url && (
                <a href={`${process.env.REACT_APP_BACKEND_URL}${inv.pdf_url}?token=${localStorage.getItem('mm_token')}`}
                  target="_blank" rel="noopener noreferrer"
                  className="transition-colors p-2 flex-shrink-0"
                  style={{ color: 'var(--text-muted)' }}
                  onMouseEnter={e => e.currentTarget.style.color = 'var(--primary)'}
                  onMouseLeave={e => e.currentTarget.style.color = 'var(--text-muted)'}
                  title="Download PDF">
                  <Download size={16} />
                </a>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
